import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import HeroSearch from "@/components/HeroSearch";
import PersonalizedHomeHero from "@/components/PersonalizedHomeHero";
import SEO from "@/components/SEO";
import { Skeleton } from "@/components/ui/skeleton";
import VetCardSkeletonList from "@/components/VetCardSkeleton";

// Lazy-load below-fold sections to reduce initial JS/CSS payload
const TrustBar = lazy(() => import("@/components/TrustBar"));
const HowItWorks = lazy(() => import("@/components/HowItWorks"));
const NewOnGetPetVetSlider = lazy(() => import("@/components/NewOnGetPetVetSlider"));
const PetCategories = lazy(() => import("@/components/PetCategories"));
const VetCards = lazy(() => import("@/components/VetCards"));
const VetCTA = lazy(() => import("@/components/VetCTA"));
const CMASection = lazy(() => import("@/components/CMASection"));
const FAQ = lazy(() => import("@/components/FAQ"));
const LocalSEO = lazy(() => import("@/components/LocalSEO"));
const Footer = lazy(() => import("@/components/Footer"));
const FindVets = lazy(() => import("@/pages/FindVets"));
const SocialProofCounter = lazy(() => import("@/components/SocialProofCounter"));
const RecentlyViewedVets = lazy(() => import("@/components/RecentlyViewedVets"));


const Index = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { hash, state } = useLocation();
  const autoSearchFromState = Boolean((state as { autoSearch?: boolean } | null)?.autoSearch);
  const [searched, setSearched] = useState(autoSearchFromState && searchParams.toString().length > 0);
  const resultsRef = useRef<HTMLDivElement>(null);
  const initialKeyRef = useRef(searchParams.toString());

  // Reliable hash-scroll: target sections may mount after first paint, so we
  // retry briefly until the element exists (or give up after ~1.5s).
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace(/^#/, "");
    if (!id) return;
    let cancelled = false;
    let attempts = 0;
    const tryScroll = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (attempts++ < 15) {
        setTimeout(tryScroll, 100);
      }
    };
    tryScroll();
    return () => {
      cancelled = true;
    };
  }, [hash]);

  const handleSearch = (params: URLSearchParams) => {
    setSearchParams(params, { replace: true });
    setSearched(true);
  };

  // Trigger inline search when the URL gains params after mount (e.g. from
  // PetCategories or in-app links pointing to /?pet=...). A fresh page load
  // on a stale URL with params does NOT auto-trigger - keeps the landing
  // page clean unless the user actively initiates a search.
  useEffect(() => {
    const current = searchParams.toString();
    if (!searched && current.length > 0 && current !== initialKeyRef.current) {
      setSearched(true);
    }
  }, [searchParams, searched]);

  useEffect(() => {
    if (searched && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [searched, searchParams]);

  // Reset embedded search results when the logo is clicked to return home.
  // Clears URL params and unmounts <FindVets/> so any "View all" cache is
  // disposed via its unmount cleanup.
  useEffect(() => {
    const handler = () => {
      setSearched(false);
      if (searchParams.toString().length > 0) {
        setSearchParams(new URLSearchParams(), { replace: true });
      }
      initialKeyRef.current = "";
    };
    window.addEventListener("getpetvet:reset-home", handler);
    return () => window.removeEventListener("getpetvet:reset-home", handler);
  }, [searchParams, setSearchParams]);

  return (
    <div className="min-h-dvh bg-background flex flex-col">
      <SEO
        title="GetPetVet - Find & Book Trusted UK Vets Online"
        description="Search local UK vet practices by postcode, compare prices, and book in-person or video consultations instantly with RCVS-registered vets."
        canonical="/"
      />

      <Navbar />
      <main id="main-content">
        <PersonalizedHomeHero />
        <HeroSearch onSearch={handleSearch} />
        <Suspense fallback={null}>
          <TrustBar />
          {searched && (
            <div ref={resultsRef} className="bg-background">
              <Suspense
                fallback={
                  <div className="container mx-auto px-4 py-8 space-y-4">
                    <Skeleton className="h-10 w-64" />
                    <VetCardSkeletonList count={5} delayMs={200} label="Loading vet results" />
                  </div>
                }
              >
                <FindVets key={searchParams.toString() || "all-vets"} embedded autoSearch />
              </Suspense>
            </div>
          )}
        </Suspense>
        <Suspense fallback={null}>
          <SocialProofCounter />
          <RecentlyViewedVets />
          <HowItWorks />

          <NewOnGetPetVetSlider />
          <PetCategories />
          <VetCards />
          <CMASection />
          <VetCTA />
          <LocalSEO />
          <FAQ />
        </Suspense>
      </main>
      <Suspense fallback={<div aria-hidden="true" className="border-t border-border bg-muted/30 mt-auto min-h-[640px] sm:min-h-[520px]" />}>
        <Footer />
      </Suspense>
    </div>
  );
};

export default Index;
